import { createReducer } from 'redux-act';
import { reject, prepend } from 'ramda';
import { SORT_MOST_POPULAR, IProduct, IChip } from '@alycecom/ui';
import { TLegacyErrors } from '@alycecom/services';

import { PRODUCTS_PER_PAGE } from '../../constants/pagination.constants';

import {
  loadProductsSuccess,
  loadProductsFail,
  setLoading,
  resetFilters,
  setFilter,
  setSearch,
  setSorting,
  setPage,
  resetProducts,
  addChip,
  deleteChip,
  clearAllChips,
  setAllFilters,
  setChips,
  setCountryId,
} from './products.actions';
import { IFilters, IPagination, ISort } from './products.types';

export interface IProductsState {
  isLoading: boolean;
  countryId?: number;
  pagination: IPagination;
  sort: ISort;
  search: string;
  filters: IFilters;
  filterChips: IChip[];
  hasMore: boolean;
  products: IProduct[];
  errors: TLegacyErrors;
}

export const initialState: IProductsState = {
  isLoading: false,
  countryId: undefined,
  pagination: {
    currentPage: 1,
    perPage: PRODUCTS_PER_PAGE,
    total: 0,
    totalPages: 0,
  },
  sort: {
    id: SORT_MOST_POPULAR.id,
    value: SORT_MOST_POPULAR.value,
  },
  search: '',
  filters: {
    brands: [],
    categories: [],
    giftTypes: [],
  },
  filterChips: [],
  hasMore: true,
  products: [],
  errors: {},
};

const reducer = createReducer<IProductsState>({}, initialState);

reducer
  .on(loadProductsSuccess, (state, payload) => ({
    ...state,
    isLoading: false,
    products: [...state.products, ...payload.products],
    pagination: payload.pagination,
    hasMore: state.pagination.currentPage < payload.pagination.totalPages,
  }))
  .on(loadProductsFail, (state, payload) => ({
    ...state,
    isLoading: false,
    errors: payload,
  }))
  .on(setLoading, (state, payload) => ({
    ...state,
    isLoading: payload,
  }));

reducer
  .on(setSearch, (state, payload) => ({
    ...state,
    search: payload,
  }))
  .on(setSorting, (state, payload) => ({
    ...state,
    sort: payload,
  }))
  .on(setPage, (state, payload) => ({
    ...state,
    isLoading: true,
    pagination: {
      ...state.pagination,
      currentPage: payload,
    },
  }))
  .on(setCountryId, (state, payload) => ({
    ...state,
    countryId: payload,
  }));

reducer
  .on(setFilter, (state, payload) => ({
    ...state,
    filters: {
      ...state.filters,
      [payload.key]: payload.items,
    },
  }))
  .on(setAllFilters, (state, payload) => ({
    ...state,
    filters: payload,
  }))
  .on(resetFilters, state => ({
    ...state,
    search: '',
    filters: {
      ...initialState.filters,
    },
  }));

reducer
  .on(addChip, (state, payload) => ({
    ...state,
    filterChips: prepend(payload, state.filterChips),
  }))
  .on(deleteChip, (state, payload) => ({
    ...state,
    filterChips: reject((chip: IChip) => chip.name === payload.name && chip.id === payload.id, state.filterChips),
  }))
  .on(clearAllChips, state => ({
    ...state,
    filterChips: [],
  }))
  .on(setChips, (state, payload) => ({
    ...state,
    filterChips: payload,
  }));

reducer.on(resetProducts, state => ({
  ...state,
  products: [],
}));

export default reducer;
